/* global Word Office console */

export async function insertText(text: string) {
  // Write text to the document.
  try {
    await Word.run(async (context) => {
      let body = context.document.body;
      body.insertParagraph(text, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
  }
}

/**
 * Insert text at the current cursor position (or replacing the current
 * selection). Used to drop a merge tag straight into the document instead of
 * making the user copy and paste it.
 *
 * The add-in runs in both Word and PowerPoint, which have different insertion
 * APIs, so branch on the host. In PowerPoint the user must have a text box or
 * placeholder in edit mode; if not, the async insert fails and we fall back to
 * copy (handled by the caller when this returns false).
 *
 * @returns true if the insert succeeded, false otherwise.
 */
export async function insertAtCursor(text: string): Promise<boolean> {
  try {
    if (Office.context.host === Office.HostType.Word) {
      await Word.run(async (context) => {
        const range = context.document.getSelection();
        range.insertText(text, Word.InsertLocation.replace);
        range.select(Word.SelectionMode.end);
        await context.sync();
      });
      return true;
    }

    // PowerPoint (and any other host that supports it): insert into the current
    // text selection via the common Office API.
    return await new Promise<boolean>((resolve) => {
      Office.context.document.setSelectedDataAsync(
        text,
        { coercionType: Office.CoercionType.Text },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.log("Insert error: " + result.error.message);
            resolve(false);
          } else {
            resolve(true);
          }
        }
      );
    });
  } catch (error) {
    console.log("Insert error: " + error);
    return false;
  }
}

/**
 * Copy plain text to the clipboard. Prefers the async Clipboard API so the
 * Office webview UI thread is never blocked (the synchronous execCommand path
 * froze the task pane for a moment on each copy); falls back to a hidden
 * textarea + execCommand where the async API is unavailable.
 *
 * @returns true if the copy succeeded, false otherwise.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.log("Clipboard writeText error: " + error);
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.cssText = "position:fixed;left:-9999px;top:0;";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (error) {
    console.log("Clipboard copy error: " + error);
    return false;
  }
}

/**
 * Copy an image to the clipboard as an HTML <img> whose src is the image URL.
 * Used for signature placeholder fields: on paste the host fetches the image,
 * so the exact pixels land in the document and Portant detects the field by
 * image content. Prefers the async Clipboard API (non-blocking); falls back to
 * a contenteditable + execCommand selection where it is unavailable.
 *
 * @returns true if the clipboard write succeeded, false otherwise.
 */
export async function copyImageHtml(url: string): Promise<boolean> {
  const html = `<img src="${url}">`;

  if (navigator.clipboard && "ClipboardItem" in window) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }) }),
      ]);
      return true;
    } catch (error) {
      console.log("Clipboard write error: " + error);
    }
  }

  try {
    const holder = document.createElement("div");
    holder.setAttribute("contenteditable", "true");
    holder.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;";
    holder.innerHTML = html;
    document.body.appendChild(holder);
    const range = document.createRange();
    range.selectNodeContents(holder);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    const ok = document.execCommand("copy");
    sel?.removeAllRanges();
    document.body.removeChild(holder);
    return ok;
  } catch (error) {
    console.log("Clipboard copy error: " + error);
    return false;
  }
}
